/**
 * Centered Cmd+K palette. Larger surface than the sidebar dropdown and
 * accessible from anywhere in the app. Both surfaces share the
 * useGlobalSearchController + GlobalSearchResults so they stay
 * behaviourally identical.
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { GlobalSearchResults } from './GlobalSearchResults';
import { useGlobalSearchController } from './useGlobalSearchController';
import styles from './GlobalSearch.module.css';

interface GlobalSearchPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export function GlobalSearchPalette({ isOpen, onClose }: GlobalSearchPaletteProps) {
  const inputRef = useRef<HTMLInputElement>(null);

  const controller = useGlobalSearchController({ onClose });

  // Reset query each time the palette opens so it starts fresh.
  useEffect(() => {
    if (isOpen) {
      controller.reset();
      requestAnimationFrame(() => inputRef.current?.focus());
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
    // controller.reset is stable across renders; intentionally omit it
    // from deps to avoid resetting on every controller-state change
    // while open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const listboxId = 'palette-gsearch-listbox';

  return createPortal(
    <div
      className={styles.paletteBackdrop}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={styles.paletteContainer}
        role="dialog"
        aria-modal="true"
        aria-label="Search companies and people"
      >
        <div className={styles.paletteInputRow}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={styles.paletteIcon}
            aria-hidden
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded={true}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              controller.flatItems.length > 0
                ? `gsearch-item-${controller.activeIndex}`
                : undefined
            }
            placeholder="Search companies and people"
            value={controller.query}
            onChange={(e) => controller.setQuery(e.target.value)}
            onKeyDown={controller.handleInputKeyDown}
            className={styles.paletteInput}
            autoComplete="off"
            spellCheck={false}
          />
          <span className={styles.paletteEscHint} aria-hidden>
            ESC
          </span>
        </div>
        <GlobalSearchResults
          search={controller.search}
          sections={controller.sections}
          activeIndex={controller.activeIndex}
          setActiveIndex={controller.setActiveIndex}
          onSelectItem={controller.selectItem}
          listboxId={listboxId}
        />
      </div>
    </div>,
    document.body,
  );
}
